import { WalletService, CommonService } from "td_server/services";
import templeLogo from "../../assets/temple-logo-icon.png";
import {
  setWalletInfo,
  setTransactions,
  setLoading,
  setTransactionsLoading,
  setRecharging,
  setError,
} from "../slices/walletSlice";
import {
  generateTransactionId,
  truncateToTwoDecimals,
} from "../../utils/HelperFunctions";
import { trackCleavertapEvent } from "../../utils/analyics/analyticsHelper";
import { ANALYTICS_EVENTS } from "../../utils/analyics/analyticsEvents";
import { generateRazorPayOrderId } from "./orderAction";

/**
 * Dynamically loads Razorpay checkout.js script if not present
 */
export const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if ((window as any).Razorpay) {
      return resolve(true);
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Fetch current wallet balance directly via td_server (reads Firestore)
 */
export const fetchWalletBalanceAction =
  (userId: string) => async (dispatch: any) => {
    if (!userId) return;
    try {
      dispatch(setLoading(true));
      const walletInfo = await WalletService.getWallet(userId);
      dispatch(setWalletInfo(walletInfo));
      dispatch(setLoading(false));
      return walletInfo;
    } catch (err: any) {
      dispatch(setLoading(false));
      dispatch(setError(err.message || "Failed to fetch wallet balance"));
    }
  };

/**
 * Fetch transaction history directly via td_server (reads Firestore)
 */
export const fetchTransactionHistoryAction =
  (userId: string, limit: number = 10) => async (dispatch: any) => {
    if (!userId) return;
    try {
      dispatch(setTransactionsLoading(true));
      const transactions = await WalletService.getTransactions(userId, limit);
      dispatch(setTransactions(transactions || []));
      dispatch(setTransactionsLoading(false));
      return transactions;
    } catch (err: any) {
      dispatch(setTransactionsLoading(false));
      dispatch(setError(err.message || "Failed to fetch transactions"));
    }
  };

/**
 * Initiate wallet recharge:
 * Implemented EXACTLY as ReviewCartScreen's placeOrder flow:
 *
 * 1. Computes razorPayAmount in subunits (paise for INR, cents for overseas)
 * 2. Prepares orderData with x-temple-dekho-signature via generateRazorPayOrderId (/payment/v2/createOrder)
 * 3. Fetches Razorpay credentials from CommonService.getRazorPayCredentials()
 * 4. Tracks Payment_Initiated analytics event via CleverTap
 * 5. Opens Razorpay modal with currency explicitly set (e.g. USD / INR) and notes
 * 6. On success, calls WalletService.verifyPayment with HMAC verification and idempotency guard
 * 7. Refreshes wallet balance and transaction list
 */
export const rechargeWalletAction =
  (
    amount: number,
    indRupees: number | string,
    userId?: string,
    userInfo?: any,
    isOverseas: boolean = false,
    rate: number = 1,
    currencyCode: string = "INR",
    currencySymbols: string = "₹",
    orderValueOv: any = null,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) =>
  async (dispatch: any) => {
    try {
      // Support both signatures:
      // New: (amount, indRupees, userId, userInfo, isOverseas, rate, currencyCode, currencySymbols, orderValueOv, onSuccess, onError)
      // Legacy: (amount, userId, userInfo, onSuccess, onError)
      let actualAmount = Number(amount);
      let actualIndRupees: number;
      let actualUserId: string;
      let actualUserInfo: any;
      let actualIsOverseas = isOverseas;
      let actualRate = rate;
      let actualCurrencyCode = currencyCode || "INR";
      let actualOnSuccess = onSuccess;
      let actualOnError = onError;

      if (typeof indRupees === "string") {
        actualUserId = indRupees;
        actualIndRupees = actualAmount;
        actualUserInfo = userId;
        actualOnSuccess = userInfo;
        actualOnError = isOverseas as any;
      } else {
        actualIndRupees = Number(indRupees) || actualAmount;
        actualUserId = userId || "";
        actualUserInfo = userInfo;
      }

      if (!actualUserId) {
        const err = "Please log in to recharge your Temple Dekho wallet.";
        if (actualOnError) actualOnError(err);
        return;
      }

      dispatch(setRecharging(true));

      const isScriptLoaded = await loadRazorpayScript();
      if (!isScriptLoaded) {
        dispatch(setRecharging(false));
        const err =
          "Razorpay SDK failed to load. Please check your internet connection.";
        if (actualOnError) actualOnError(err);
        return;
      }

      // 1. Calculate razorPayAmount in subunits (matching ReviewCartScreen lines 1190-1198)
      let razorPayAmount: number;
      if (actualIsOverseas) {
        razorPayAmount = parseFloat((actualAmount * 100).toFixed(2));
      } else {
        razorPayAmount = Number(Number(actualIndRupees) * 100);
      }

      // 2. Generate unique Transaction and Order IDs (matching ReviewCartScreen lines 1200-1201)
      const transectionId = generateTransactionId();
      const orderId = `wallet_${transectionId}`;

      // 3. Construct orderData payload (matching ReviewCartScreen lines 1203-1215)
      const orderData = {
        amount: razorPayAmount.toString(),
        currency: actualIsOverseas ? actualCurrencyCode : "INR",
        receipt: transectionId,
        notes: {
          key1: orderId,
          key2: actualUserInfo?.phoneNumber || "",
          key3: "dev",
          paymentType: "WALLET_TOPUP",
          userId: actualUserId,
          txnId: transectionId,
          coins: actualIndRupees.toString(),
          amount: actualAmount.toString(),
          indRupees: actualIndRupees.toString(),
          currency: actualIsOverseas ? actualCurrencyCode : "INR",
        },
      };

      // 4. Dispatch generateRazorPayOrderId (hits /payment/v2/createOrder with x-temple-dekho-signature)
      // Matching ReviewCartScreen lines 1218-1219
      const rpOrderIdResponse = await dispatch(generateRazorPayOrderId(orderData));
      const rpOrderId = rpOrderIdResponse?.id;

      if (!rpOrderId) {
        throw new Error("Failed to generate payment order. Please try again.");
      }

      // 5. Track Payment_Initiated event (matching ReviewCartScreen lines 1220-1231)
      trackCleavertapEvent(ANALYTICS_EVENTS.Payment_Initiated, {
        User_ID: actualUserId ?? "Guest User",
        Order_ID: orderId,
        Transaction_ID: transectionId,
        Amount:
          truncateToTwoDecimals((rpOrderIdResponse?.amount || razorPayAmount) / 100) ?? 0,
        Currency: actualIsOverseas ? actualCurrencyCode : "INR",
        IsOverseas: actualIsOverseas,
        Order_Type: "wallet_recharge",
        rate: actualRate,
        Payment_Gateway: "Razorpay",
      });

      // 6. Fetch Razorpay credentials (matching ReviewCartScreen line 1234)
      const rpSecrets = await CommonService.getRazorPayCredentials();
      const rpCredentials = rpSecrets?.credentials;

      if (!rpCredentials?.KEY) {
        throw new Error("Unable to retrieve payment gateway credentials.");
      }

      // Prefill user contact details (matching ReviewCartScreen lines 1414-1421)
      const personalInfo = actualUserInfo?.personalInfo || {};
      const userName = personalInfo.firstName
        ? `${personalInfo.firstName} ${personalInfo.lastName || ""}`.trim()
        : "Temple Dekho Devotee";
      const userContact = actualUserInfo?.phoneNumber || "";
      const userEmail = personalInfo.email || "";

      // 7. Configure Razorpay modal options (matching ReviewCartScreen lines 1305-1435)
      const options = {
        key: rpCredentials.KEY,
        key_secreat: rpCredentials.SECRET,
        order_id: rpOrderId,
        amount: razorPayAmount.toString(),
        currency: actualIsOverseas ? actualCurrencyCode : "INR",
        name: "Temple Dekho",
        description: "",
        image: templeLogo,
        handler: async function (response: any) {
          if (
            response.razorpay_payment_id &&
            response.razorpay_payment_id.length > 0
          ) {
            try {
              // 8. Verify payment on td_node_server via WalletService.verifyPayment
              // Protected by x-temple-dekho-signature, Razorpay HMAC, and atomic credit
              const verificationPayload = {
                razorpay_order_id: response.razorpay_order_id || rpOrderId,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                txnId: transectionId,
                userId: actualUserId,
              };

              const verificationResponse = await WalletService.verifyPayment(
                verificationPayload
              );

              // 9. Refresh balance and transactions on success
              await dispatch(fetchWalletBalanceAction(actualUserId));
              await dispatch(fetchTransactionHistoryAction(actualUserId, 10));
              dispatch(setRecharging(false));

              if (actualOnSuccess) {
                actualOnSuccess(verificationResponse);
              }
            } catch (verifyErr: any) {
              dispatch(setRecharging(false));
              if (actualOnError) {
                actualOnError(verifyErr.message || "Payment verification failed");
              }
            }
          } else {
            dispatch(setRecharging(false));
            if (actualOnError) {
              actualOnError("Payment failed or cancelled");
            }
          }
        },
        prefill: {
          name: userName,
          contact: userContact,
          email: userEmail,
        },
        notes: {
          paymentType: "WALLET_TOPUP",
          userId: actualUserId,
          txnId: transectionId,
          coins: actualIndRupees.toString(),
          amount: actualAmount.toString(),
          indRupees: actualIndRupees.toString(),
          currency: actualIsOverseas ? actualCurrencyCode : "INR",
        },
        theme: {
          color: "#F37254",
        },
        modal: {
          escape: false,
          ondismiss: function () {
            dispatch(setRecharging(false));
            if (actualOnError) {
              actualOnError("Payment cancelled");
            }
          },
        },
      };

      const pay = new (window as any).Razorpay(options);

      pay.on("modal.closed", () => {
        dispatch(setRecharging(false));
      });

      pay.on("payment.failed", function (response: any) {
        dispatch(setRecharging(false));
        trackCleavertapEvent(ANALYTICS_EVENTS.Payment_Failed, {
          User_ID: actualUserId ?? "Guest User",
          Cart_ID: "wallet_recharge",
          Order_ID: orderId,
          Transaction_ID: transectionId,
          Currency: actualIsOverseas ? actualCurrencyCode : "INR",
          Failure_Code: response?.error?.code ?? "",
          Failure_Reason: response?.error?.reason ?? "",
          Failure_Message: response?.error?.description ?? "",
          Payment_Method: response?.error?.source ?? "",
          Amount: razorPayAmount.toString(),
          rate: actualRate,
        });
        const desc = response?.error?.description || "Payment failed";
        if (actualOnError) actualOnError(desc);
      });

      pay.open();
    } catch (err: any) {
      dispatch(setRecharging(false));
      if (onError) {
        onError(err.message || "Failed to initiate recharge");
      }
    }
  };
